import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Heart, Users, Globe, Sparkles, Target, Star, Award, PlaneTakeoff } from 'lucide-react';
import PageSeo from '@/components/PageSeo';

export default function OurCharter() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const principles = [
    {
      icon: Heart,
      title: "Accessibility First",
      description: "We believe travel is a fundamental human desire, not a luxury reserved for the wealthy. Every feature, every policy, and every decision we make starts with one question: does this make travel more accessible? We remove financial barriers—no credit checks, no hidden fees, no surprise charges. Our installment model mirrors how people actually manage money: in small, manageable chunks. We design for the restaurant server saving for a family reunion, the warehouse worker visiting aging parents, and the gig economy hustler chasing opportunity across borders."
    },
    {
      icon: Users,
      title: "Customer-Centric Always",
      description: "We don't have 'users' or 'accounts'—we have people with dreams, obligations, and constraints. Every support inquiry is answered by humans who actually care. Every feature is tested with real travelers in real situations. We track satisfaction scores obsessively because they reveal truth that revenue metrics can't. When we hit 4.9/5 satisfaction, we didn't celebrate—we studied the 0.1 gap. What disappointed that person? How do we fix it? Our success is measured by the grandmother who hugged her grandchildren for the first time in years, enabled by a platform that didn't exclude her."
    },
    {
      icon: Globe,
      title: "Global Inclusion",
      description: "Travel connects cultures, families, and opportunities. But traditional travel booking excludes billions through credit score requirements, currency barriers, and Western-centric design. We build for a global audience from day one. Our platform supports travelers from over 120 countries. We partner with airlines across continents. We design interfaces that work on budget Android phones with spotty internet, not just premium devices on fiber connections. Inclusion isn't a marketing campaign—it's an engineering requirement."
    },
    {
      icon: Sparkles,
      title: "Relentless Innovation",
      description: "The travel industry moves slowly. Legacy systems, entrenched players, and regulatory complexity create inertia. We move fast. We ship features in weeks, not quarters. We leverage AI for smarter search, better pricing, and automated support. We build Progressive Web Apps when native apps would take months. We integrate new payment methods before they're mainstream. Innovation isn't about flashy technology—it's about removing friction between people and their goals. Every hour we shave from the booking process. Every click we eliminate. Every moment of confusion we prevent. That's innovation."
    },
    {
      icon: Target,
      title: "Sustainable Growth",
      description: "We didn't raise venture capital to achieve hockey-stick growth at any cost. We're building a business that lasts, that treats employees fairly, that serves customers honestly. Rapid growth means nothing if it destroys culture or forces compromises on values. We hire slowly and deliberately. We partner with airlines that share our values. We turn down opportunities that would compromise our mission. Financial sustainability enables long-term impact. We're not here to flip the company in three years—we're here to change an industry permanently."
    }
  ];

  const commitments = [
    {
      title: "We will never sell your data",
      description: "Your travel plans, payment information, and personal details are yours. We don't sell data to advertisers, we don't share it with data brokers, and we don't monetize your privacy. Our business model is simple: we earn revenue by facilitating travel bookings, not by exploiting your information."
    },
    {
      title: "We will honor our price locks",
      description: "When we lock in your price, that's a promise. Market volatility, fuel surcharges, carrier fare increases—none of that gets passed to you. If we quote a price, we honor it. No exceptions, no fine print, no 'dynamic pricing adjustments.' Your price is your price."
    },
    {
      title: "We will communicate clearly during disruptions",
      description: "Flight delays, cancellations, and itinerary changes happen. When they do, we won't hide behind airlines or leave you stranded. You'll receive immediate notifications, clear explanations of your options, and direct access to support that can actually help. Disruptions are stressful enough without adding communication breakdowns."
    },
    {
      title: "We will never require credit checks",
      description: "Your credit score says nothing about your desire to visit loved ones or chase opportunities. We will never implement credit checks, hard inquiries, or scoring systems that exclude people. A valid payment method for your deposit is all you need. That's our commitment, permanently."
    },
    {
      title: "We will continuously improve",
      description: "This charter isn't static. As we learn from customers, as markets evolve, and as new challenges emerge, we'll adapt. But adaptation doesn't mean abandonment of principles—it means finding better ways to live them. We'll publish updates to this charter when significant changes occur, and we'll always explain our reasoning."
    }
  ];

  return (
    <div className="min-h-screen bg-black text-white/80">
      <PageSeo path="/charter" />
      <div className="mx-auto max-w-5xl px-6 py-12">
        {/* Back Button */}
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-white/60 hover:text-white transition-colors mb-8 text-sm"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Link>

        {/* Header */}
        <div className="mb-16">
          <h1 className="text-4xl md:text-5xl font-light text-white mb-4 tracking-tight">
            Our Charter
          </h1>
          <p className="text-base text-white/80 max-w-3xl leading-relaxed">
            The principles and commitments that guide everything we build at Defera. This is who we are, what we believe, and what you can hold us accountable to.
          </p>
        </div>

        {/* Mission Statement */}
        <div className="bg-white/5 border border-white/10 rounded-xl p-8 md:p-10 mb-20">
          <h2 className="text-2xl font-light text-white mb-4">Our Mission</h2>
          <p className="text-base text-white/80 leading-relaxed mb-4">
            Make air travel accessible and affordable for everyone through transparent pricing, flexible payments, and radical inclusion.
          </p>
          <p className="text-sm text-white/80 leading-relaxed">
            We exist because millions of people want to travel but face financial barriers. Credit checks exclude them. Upfront costs strand them. Hidden fees surprise them. Traditional travel booking wasn't built for working-class families, students, gig workers, or anyone living paycheck to paycheck. We're changing that.
          </p>
        </div>

        {/* Core Principles */}
        <div className="mb-20">
          <h2 className="text-2xl font-light text-white mb-10">Core Principles</h2>
          <div className="space-y-10">
            {principles.map((principle, index) => (
              <div key={index} className="border-l-2 border-[var(--accent-color)]/30 pl-6">
                <div className="flex items-center gap-3 mb-3">
                  <principle.icon className="w-6 h-6 text-[var(--accent-color)]" />
                  <h3 className="text-xl font-light text-white">{principle.title}</h3>
                </div>
                <p className="text-sm text-white/80 leading-relaxed">
                  {principle.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Commitments */}
        <div className="mb-12">
          <h2 className="text-2xl font-light text-white mb-10">Our Commitments</h2>
          <div className="space-y-6">
            {commitments.map((commitment, index) => (
              <div 
                key={index}
                className="bg-white/5 border border-white/10 rounded-lg p-6 hover:bg-white/[0.07] transition-colors"
              >
                <h3 className="text-base font-normal text-white mb-2">
                  {commitment.title}
                </h3>
                <p className="text-sm text-white/80 leading-relaxed">
                  {commitment.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Accountability */}
        <div className="bg-white/5 border border-white/10 rounded-xl p-8 mt-16">
          <h3 className="text-lg font-light text-white mb-3">Hold Us Accountable</h3>
          <p className="text-sm text-white/80 leading-relaxed mb-4">
            This charter means nothing if we don't live it. If you experience something that contradicts these principles, we want to know. Email us at <span className="text-[var(--accent-color)]">charter@defera.app</span> with concerns, feedback, or suggestions. We read every message and we respond personally.
          </p>
          <p className="text-sm text-white/80 leading-relaxed">
            We publish quarterly transparency reports detailing customer satisfaction scores, complaint resolution times, and any instances where we fell short of these commitments. Accountability isn't optional—it's foundational.
          </p>
        </div>

        {/* Oscar-Style Award Banners */}
        <div className="mt-12 space-y-4">
          {/* Reward System Banner */}
          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-brand-900/20 via-brand-900/10 to-brand-800/20 border-2 border-brand-500/30 backdrop-blur-xl">
            <div className="absolute inset-0 bg-gradient-to-r from-brand-400/5 via-brand-400/5 to-brand-400/5" />
            <div className="relative px-6 py-4 flex items-center gap-4">
              {/* Trophy Icon */}
              <div className="relative">
                <div className="absolute inset-0 bg-brand-400/20 rounded-full blur-xl" />
                <div className="relative w-12 h-12 flex items-center justify-center rounded-full bg-gradient-to-br from-brand-400 via-brand-400 to-brand-500 shadow-lg shadow-brand-500/50">
                  <Award className="w-6 h-6 text-brand-900" />
                </div>
                {/* Shine Effect */}
                <div className="absolute top-0 right-0 w-3 h-3 bg-white/60 rounded-full blur-sm" />
              </div>

              {/* Text Content */}
              <div className="flex-1">
                <p className="text-sm md:text-base font-normal text-brand-100 leading-relaxed">
                  <span className="font-medium text-brand-200">Defera</span> is the world's first travel layaway service that offers a <span className="font-medium text-brand-200">reward system</span>
                </p>
                <p className="text-xs text-brand-300/60 mt-1 hidden md:block">
                  Triple dip by earning credit card points, frequent flyer miles, and Defera Miles
                </p>
              </div>

              {/* Decorative Stars */}
              <div className="hidden md:flex gap-1">
                <Star className="w-4 h-4 text-brand-400 fill-brand-400" />
                <Star className="w-4 h-4 text-brand-400 fill-brand-400" />
                <Star className="w-4 h-4 text-brand-400 fill-brand-400" />
              </div>
            </div>
          </div>

          {/* Semi-Private Flights Banner */}
          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-brand-900/20 via-brand-900/10 to-brand-800/20 border-2 border-brand-500/30 backdrop-blur-xl">
            <div className="absolute inset-0 bg-gradient-to-r from-brand-400/5 via-brand-400/5 to-brand-400/5" />
            <div className="relative px-6 py-4 flex items-center gap-4">
              {/* Plane Icon */}
              <div className="relative">
                <div className="absolute inset-0 bg-brand-400/20 rounded-full blur-xl" />
                <div className="relative w-12 h-12 flex items-center justify-center rounded-full bg-gradient-to-br from-brand-400 via-brand-400 to-brand-500 shadow-lg shadow-brand-500/50">
                  <PlaneTakeoff className="w-6 h-6 text-brand-900" />
                </div>
                {/* Shine Effect */}
                <div className="absolute top-0 right-0 w-3 h-3 bg-white/60 rounded-full blur-sm" />
              </div>

              {/* Text Content */}
              <div className="flex-1">
                <p className="text-sm md:text-base font-normal text-brand-100 leading-relaxed">
                  <span className="font-medium text-brand-200">Defera</span> is the world's first travel layaway service that offers <span className="font-medium text-brand-200">semi-private flights</span>
                </p>
                <p className="text-xs text-brand-300/60 mt-1 hidden md:block">
                  Book JSX semi-private flights with flexible payment plans
                </p>
              </div>

              {/* Decorative Stars */}
              <div className="hidden md:flex gap-1">
                <Star className="w-4 h-4 text-brand-400 fill-brand-400" />
                <Star className="w-4 h-4 text-brand-400 fill-brand-400" />
                <Star className="w-4 h-4 text-brand-400 fill-brand-400" />
              </div>
            </div>
          </div>
        </div>

        {/* Last Updated */}
        <div className="mt-12 pt-6 border-t border-white/5">
          <p className="text-xs text-white/40 text-center">
            Last updated: January 2025
          </p>
        </div>
      </div>
    </div>
  );
}
